import React, { useMemo } from 'react';
import { Card, Typography, Box, Stack, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import rise from '@iconify/icons-ant-design/rise';
import { BaseChart } from 'src/components/charts/BaseOptionChart';
import { isSameDay } from 'date-fns';

// Using a simple rupee symbol instead of icon
const RupeeIcon = () => (
  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#3182CE' }}>₹</span>
);

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.lighter}15 50%, ${theme.palette.primary.light}08 100%)`,
  border: `1px solid ${theme.palette.primary.light}30`,
  textAlign: 'center',
  height: '100%',
  minHeight: { xs: '100px', sm: '110px', md: '120px' },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2.5),
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}50`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
  },
}));

// Process revenue data from transactions
const processRevenueData = (apiData, timeInterval) => {
  if (!apiData || !Array.isArray(apiData)) {
    return { data: [], labels: [] };
  }

  const { start, end } = timeInterval;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  // Initialize arrays for each day
  const totalRevenue = new Array(days).fill(0);
  const cardRevenue = new Array(days).fill(0);
  const walletRevenue = new Array(days).fill(0);
  const rfidRevenue = new Array(days).fill(0);
  const labels = [];

  // Generate labels with dates for comparison
  const labelDates = [];
  for (let i = 0; i < days; i++) {
    const currentDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    labelDates.push(currentDate);
    labels.push(currentDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }));
  }

  // Process transactions
  apiData.forEach((txn) => {
    // Parse the raw MySQL UTC timestamp explicitly as UTC (append 'Z').
    const rawTime = txn.start_time || txn.created_at;
    const txnDate = new Date(rawTime.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1') + 'Z');
    
    // Check if transaction is within our date range
    if (txnDate >= startDate && txnDate <= endDate) {
      const dayIndex = Math.floor((txnDate - startDate) / (24 * 60 * 60 * 1000));
      
      if (dayIndex >= 0 && dayIndex < days) {
        // totalAmountCharged is already the net amount (refunds already deducted)
        const netAmount = parseFloat(txn.totalAmountCharged || 0);
        
        // Only add positive amounts to revenue
        if (netAmount > 0) {
          totalRevenue[dayIndex] += netAmount;
          
          if (txn.payment_type === "CARD") {
            cardRevenue[dayIndex] += netAmount;
          } else if (txn.payment_type === "WALLET") {
            walletRevenue[dayIndex] += netAmount;
          } else if (txn.payment_type === "RFID") {
            rfidRevenue[dayIndex] += netAmount;
          }
        }
      }
    }
  });

  // Remove only the last date if it's in the future (not today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let filteredLabels = labels;
  let filteredTotalRevenue = totalRevenue;
  let filteredCardRevenue = cardRevenue;
  let filteredWalletRevenue = walletRevenue;
  let filteredRfidRevenue = rfidRevenue;

  // Check only the last date
  if (labelDates.length > 0) {
    const lastDate = new Date(labelDates[labelDates.length - 1]);
    lastDate.setHours(0, 0, 0, 0);
    
    // Remove last date only if it's in the future (strictly greater than today)
    if (lastDate > today) {
      filteredLabels = labels.slice(0, -1);
      filteredTotalRevenue = totalRevenue.slice(0, -1);
      filteredCardRevenue = cardRevenue.slice(0, -1);
      filteredWalletRevenue = walletRevenue.slice(0, -1);
      filteredRfidRevenue = rfidRevenue.slice(0, -1);
    }
  }

  return {
    data: {
      totalRevenue: filteredTotalRevenue,
      cardRevenue: filteredCardRevenue,
      walletRevenue: filteredWalletRevenue,
      rfidRevenue: filteredRfidRevenue,
      labels: filteredLabels
    }
  };
};

// Chart options for revenue
const getChartOptions = (labels, yAxisTitle = 'Revenue (Rs)') => ({
  chart: {
    type: 'line',
    height: 300,
    toolbar: { show: false },
    zoom: { enabled: false },
    pan: { enabled: false },
    selection: { enabled: false },
    brush: { enabled: false },
    animations: { enabled: true },
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    padding: {
      top: 50,
      right: 20,
      bottom: 20,
      left: 20
    }
  },
  colors: ['#3182CE', '#38A169', '#ED8936', '#805AD5'],
  title: {
    align: 'left',
    style: {
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#718096'
    }
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  markers: {
    size: 4,
    strokeWidth: 2,
    hover: {
      size: 6,
    },
  },
  xaxis: {
    categories: labels,
    title: {
      text: 'Date',
      align: 'left',
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#718096'
      },
    },
    labels: {
      align: 'left',
      style: {
        fontSize: '11px',
        fontWeight: 400,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: '#4A5568'
      }
    }
  },
  yaxis: {
    title: {
      text: yAxisTitle,
      align: 'left',
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#718096'
      },
    },
    labels: {
      align: 'left',
      style: {
        fontSize: '11px',
        fontWeight: 400,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: '#4A5568'
      },
      formatter: (value) => `${value.toLocaleString()}`
    }
  },
  grid: {
    show: true,
    borderColor: '#E2E8F0',
    strokeDashArray: 3,
    xaxis: {
      lines: {
        show: false
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    }
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left',
    fontSize: '12px',
    fontWeight: 500,
    markers: {
      radius: 5,
    },
    itemMargin: {
      horizontal: 12,
      vertical: 8,
    },
    labels: {
      colors: '#4A5568'
    },
    offsetY: 0,
    floating: false,
    width: '100%',
    height: 'auto',
    formatter: function(seriesName, opts) {
      return seriesName;
    },
    onItemClick: {
      toggleDataSeries: true
    },
    onItemHover: {
      highlightDataSeries: true
    }
  },
  plotOptions: {
    line: {
      dataLabels: {
        enabled: false
      }
    }
  },
  tooltip: {
    style: {
      fontSize: '12px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    y: {
      formatter: (value) => `₹${value.toLocaleString()}`
    },
    followCursor: true,
    offsetY: -10,
    offsetX: 0,
    fixed: {
      enabled: false
    },
    position: 'top',
    custom: function({ series, seriesIndex, dataPointIndex, w }) {
      const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
      const value = data ? `₹${data.toLocaleString()}` : '₹0';
      const seriesName = w.globals.initialSeries[seriesIndex].name || 'Revenue';
      return `
        <div style="
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          max-width: 200px;
          word-wrap: break-word;
          z-index: 1000;
        ">
          <div style="font-weight: 600; margin-bottom: 4px;">${seriesName}</div>
          <div>${value}</div>
        </div>
      `;
    }
  },
  dataLabels: {
    enabled: false
  }
});

const RevenueTrends = ({ 
  timeInterval, 
  transactionsData, 
  isLoading = false, 
  error = null
}) => {
  // Process revenue data
  const revenueData = useMemo(() => {
    if (!transactionsData) {
      return null;
    }
    
    // Convert object to array if needed
    let transactionsArray;
    
    if (Array.isArray(transactionsData)) {
      transactionsArray = transactionsData;
    } else if (transactionsData && typeof transactionsData === 'object') {
      transactionsArray = Object.values(transactionsData);
    } else {
      return null;
    }
    
    if (!Array.isArray(transactionsArray) || transactionsArray.length === 0) {
      return null;
    }

    return processRevenueData(transactionsArray, timeInterval);
  }, [transactionsData, timeInterval]);

  const chartData = useMemo(() => {
    if (!revenueData || !revenueData.data) {
      // Return empty data when no transactions
      const days = 7; // Default to 7 days
      const emptyData = new Array(days).fill(0);
      const labels = [];
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const labelDatesForEmpty = [];
      for (let i = 0; i < days; i++) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - (days - 1 - i));
        currentDate.setHours(0, 0, 0, 0);
        labelDatesForEmpty.push(currentDate);
        labels.push(currentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }));
      }
      
      // Remove only the last date if it's in the future (not today)
      if (labelDatesForEmpty.length > 0) {
        const lastDate = new Date(labelDatesForEmpty[labelDatesForEmpty.length - 1]);
        lastDate.setHours(0, 0, 0, 0);
        if (lastDate > today) {
          labels.pop();
        }
      }
      
      // Adjust emptyData array length to match labels
      const adjustedEmptyData = new Array(labels.length).fill(0);
      
      return {
        series: [
          {
            name: 'Total Revenue',
            data: adjustedEmptyData,
            color: '#3182CE',
          },
          {
            name: 'Card Payments',
            data: adjustedEmptyData,
            color: '#38A169',
          },
          {
            name: 'Wallet Payments',
            data: adjustedEmptyData,
            color: '#ED8936',
          },
          {
            name: 'RFID Payments',
            data: adjustedEmptyData,
            color: '#805AD5',
          },
        ],
        options: getChartOptions(labels, 'Revenue (Rs)')
      };
    }

    const { totalRevenue, cardRevenue, walletRevenue, rfidRevenue, labels } = revenueData.data;
    
    const datasets = [
      {
        name: 'Total Revenue',
        data: totalRevenue,
        color: '#3182CE',
      },
      {
        name: 'Card Payments',
        data: cardRevenue,
        color: '#38A169',
      },
      {
        name: 'Wallet Payments',
        data: walletRevenue,
        color: '#ED8936',
      },
      {
        name: 'RFID Payments',
        data: rfidRevenue,
        color: '#805AD5',
      },
    ];

    return {
      series: datasets,
      options: getChartOptions(labels, 'Revenue (Rs)')
    };
  }, [revenueData]);

  // Calculate metrics
  const totalRevenue = revenueData?.data?.totalRevenue?.reduce((sum, val) => sum + val, 0) || 0;
  const totalCardRevenue = revenueData?.data?.cardRevenue?.reduce((sum, val) => sum + val, 0) || 0;
  const totalWalletRevenue = revenueData?.data?.walletRevenue?.reduce((sum, val) => sum + val, 0) || 0;
  const totalRfidRevenue = revenueData?.data?.rfidRevenue?.reduce((sum, val) => sum + val, 0) || 0;
  const avgDailyRevenue = Math.round(totalRevenue / (revenueData?.data?.totalRevenue?.length || 1));

  // Loading state
  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading revenue data...
            </Typography>
          </Stack>
        </Box>
      </StyledCard>
    );
  }

  // Error state
  if (error) {
    return (
      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load revenue data. Please try again.
          </Alert>
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
          }}
        >
          Revenue Trends
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
          }}
        >
          Track revenue performance over time
        </Typography>

        {/* Key Metrics - 3 Rows */}
        <Box sx={{ mb: 1 }}>
          {/* First Row */}
          <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: 'stretch' }}>
            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                  }}
                >
                  ₹{totalRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                }}
              >
                Total Revenue
              </Typography>
            </MetricCard>

            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                  }}
                >
                  ₹{avgDailyRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                }}
              >
                Avg Daily
              </Typography>
            </MetricCard>
          </Stack>

          {/* Second Row */}
          {/* <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ₹{totalCardRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Card Payments
              </Typography>
            </MetricCard>

            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ₹{totalWalletRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Wallet Payments
              </Typography>
            </MetricCard>
          </Stack> */}

          {/* Third Row */}
          {/* <Stack direction="row" spacing={2} sx={{ alignItems: 'stretch' }}>
            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ₹{totalRfidRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                RFID Payments
              </Typography>
            </MetricCard>
          </Stack> */}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ 
        flex: 1, 
        mt: 1, 
        p: { xs: 1, sm: 2 }, 
        pb: { xs: 2, sm: 3 },
        overflow: 'hidden',
        width: '100%'
      }}>
        <Box sx={{ 
          width: '100%', 
          height: { xs: 250, sm: 300, md: 350 },
          overflow: 'hidden',
          position: 'relative'
        }}>
          <BaseChart
            key={`revenue-chart-${transactionsData?.length || 0}-${timeInterval?.type || 'default'}`}
            series={chartData.series}
            options={chartData.options}
            type="line"
            height="100%"
            width="100%"
          />
        </Box>
      </Box>
    </StyledCard>
  );
};

export default RevenueTrends;
